import React from 'react';
import { Target, RefreshCw, CheckCircle2, FileText, AlertTriangle } from 'lucide-react';
import { OrkaSurface } from '../OrkaSurface';
import { OrkaText } from '../OrkaText';
import { OrkaIcon } from '../OrkaIcon';
import { OrkaButton } from '../OrkaButton';

/**
 * FocusStripWidget — compact widget row (V3 Dashboard refactor).
 *
 * Replaces the large NowPane + RecoveryPane split that was dominating the
 * daily dashboard and pushing Day Planner / Unscheduled / Backlog below the
 * fold. Shows three tight widgets:
 *   1. Now Focus    — single protected task + "Start focus"
 *   2. Recovery     — counts for slipped/partial/blocked + "Start EOD"
 *   3. EOD / Report — quick link to today's EOD report (if available)
 *
 * All widgets fit inside a single ~112px strip and share the V3 tokens.
 */

function Widget({ title, icon: Icon, intent = 'focus', children, testId }) {
  return (
    <OrkaSurface
      variant="raised"
      radius="lg"
      bordered
      className="flex flex-col gap-1.5 px-3 py-2.5 h-full min-h-0"
      data-intent={intent}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        {Icon && <OrkaIcon icon={Icon} size="sm" intent="intent" />}
        <OrkaText size="micro" variant="tertiary" as="span" className="uppercase tracking-wider font-semibold truncate">
          {title}
        </OrkaText>
      </div>
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        {children}
      </div>
    </OrkaSurface>
  );
}

function NowWidget({ task, onStartFocus, onOpenTask }) {
  if (!task) {
    return (
      <Widget title="Now · Focus" icon={Target} intent="focus" testId="focus-strip-now">
        <OrkaText size="small" variant="tertiary">No task protected</OrkaText>
      </Widget>
    );
  }
  return (
    <Widget title="Now · Focus" icon={Target} intent="focus" testId="focus-strip-now">
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          onClick={() => onOpenTask?.(task)}
          className="flex-1 min-w-0 text-left truncate text-sm orka-text-primary font-medium hover:underline"
          data-testid="focus-strip-now-task"
          title={task.task_name || task.title}
        >
          {task.task_name || task.title || 'Untitled task'}
        </button>
        <OrkaButton
          variant="primary"
          size="sm"
          icon={Target}
          onClick={() => onStartFocus?.(task)}
          data-testid="focus-strip-now-start"
        >
          Start
        </OrkaButton>
      </div>
    </Widget>
  );
}

function RecoveryWidget({ counts = {}, partialTasks = [], partialCount = 0, onOpenPartials, onOpenBlocked, onRecoverTask }) {
  const partial = counts.partial || 0;
  const blocked = counts.blocked || 0;
  const slipped = counts.slipped || 0;
  const [tab, setTab] = React.useState(partial > 0 ? 'partials' : 'all');
  const total = partial + blocked + slipped;

  const TabBtn = ({ value, label, count }) => (
    <button
      type="button"
      onClick={() => setTab(value)}
      className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full orka-transition-colors ${
        tab === value
          ? 'orka-bg-warning-soft orka-text-warning'
          : 'orka-text-tertiary hover:orka-text-secondary'
      }`}
      data-testid={`focus-strip-recovery-tab-${value}`}
    >
      {label}{count != null ? ` (${count})` : ''}
    </button>
  );

  return (
    <Widget title="Recovery · Signals" icon={RefreshCw} intent="recover" testId="focus-strip-recovery">
      {total === 0 && partialCount === 0 ? (
        <OrkaText size="small" variant="tertiary">Nothing to recover.</OrkaText>
      ) : (
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-1 min-w-0 flex-wrap">
            <TabBtn value="partials" label="Partials" count={partial} />
            <TabBtn value="blocked" label="Blocked" count={blocked} />
            {slipped > 0 && <TabBtn value="slipped" label="Slipped" count={slipped} />}
            <TabBtn value="all" label="All" />
            {partialCount > 0 && (
              <button
                type="button"
                onClick={onOpenPartials}
                className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full orka-bg-warning-soft border border-amber-300 text-amber-900 dark:text-amber-100 text-xs font-bold shadow-sm hover:shadow-md hover:scale-[1.03] orka-transition"
                data-testid="focus-strip-partials-link"
                title={`${partialCount} partial / in-progress task${partialCount === 1 ? '' : 's'} — click to review`}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>{partialCount} partial</span>
              </button>
            )}
          </div>
          {tab === 'partials' && (
            <div className="flex items-center justify-between gap-1.5 min-w-0" data-testid="focus-strip-recovery-partials-panel">
              {partial === 0 ? (
                <OrkaText size="micro" variant="tertiary">No partial tasks.</OrkaText>
              ) : (
                <button
                  type="button"
                  onClick={() => onRecoverTask?.(partialTasks[0]) || onOpenPartials?.()}
                  className="flex-1 min-w-0 truncate text-left text-xs orka-text-primary font-medium hover:underline"
                  title={partialTasks[0]?.task_title || partialTasks[0]?.title}
                  data-testid="focus-strip-recovery-partial-task"
                >
                  {partialTasks[0]?.task_title || partialTasks[0]?.title || `${partial} partial`}
                </button>
              )}
              <OrkaButton
                variant="secondary"
                size="sm"
                icon={CheckCircle2}
                onClick={() => onOpenPartials?.()}
                data-testid="focus-strip-recovery-review-partials"
              >
                Review
              </OrkaButton>
            </div>
          )}
          {tab === 'blocked' && (
            <div className="flex items-center justify-between gap-1.5 min-w-0" data-testid="focus-strip-recovery-blocked-panel">
              <OrkaText size="small" variant="secondary">
                <span className="font-bold orka-text-primary">{blocked}</span> blocked task{blocked === 1 ? '' : 's'}
              </OrkaText>
              <OrkaButton variant="secondary" size="sm" onClick={() => onOpenBlocked?.()} data-testid="focus-strip-recovery-review-blocked">
                Review
              </OrkaButton>
            </div>
          )}
          {tab === 'slipped' && (
            <OrkaText size="small" variant="secondary">
              <span className="font-bold orka-text-primary">{slipped}</span> slipped task{slipped === 1 ? '' : 's'}
            </OrkaText>
          )}
          {tab === 'all' && (
            <div className="flex items-center gap-2 text-xs orka-text-secondary min-w-0 flex-wrap">
              {partial > 0 && (
                <span className="font-mono" data-testid="focus-strip-recovery-partial">
                  <span className="font-bold orka-text-primary">{partial}</span> partial
                </span>
              )}
              {blocked > 0 && (
                <span className="font-mono" data-testid="focus-strip-recovery-blocked">
                  <span className="font-bold orka-text-primary">{blocked}</span> blocked
                </span>
              )}
              {slipped > 0 && (
                <span className="font-mono" data-testid="focus-strip-recovery-slipped">
                  <span className="font-bold orka-text-primary">{slipped}</span> slipped
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </Widget>
  );
}

function EODWidget({ eodSummary, onOpenEOD, onStartEOD, onOpenDoneReport }) {
  return (
    <Widget title="Report · End Day" icon={FileText} intent="plan" testId="focus-strip-report">
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 text-xs orka-text-secondary min-w-0">
          {eodSummary ? (
            <span className="inline-flex items-center gap-1 orka-text-success font-medium truncate">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">EOD filed</span>
            </span>
          ) : (
            <OrkaText size="small" variant="tertiary">No EOD filed yet</OrkaText>
          )}
        </div>
        <div className="flex items-center gap-1">
          {onOpenDoneReport && (
            <OrkaButton
              variant="secondary"
              size="sm"
              icon={FileText}
              onClick={onOpenDoneReport}
              data-testid="focus-strip-done-report"
            >
              Done
            </OrkaButton>
          )}
          {eodSummary && onOpenEOD ? (
            <OrkaButton variant="secondary" size="sm" icon={FileText} onClick={onOpenEOD} data-testid="focus-strip-open-eod">
              View EOD
            </OrkaButton>
          ) : onStartEOD ? (
            <OrkaButton variant="primary" size="sm" icon={CheckCircle2} onClick={onStartEOD} data-testid="focus-strip-start-eod">
              End Day
            </OrkaButton>
          ) : null}
        </div>
      </div>
    </Widget>
  );
}

export function FocusStripWidget({
  focusTask,
  counts,
  partialTasks = [],
  eodSummary,
  partialCount = 0,
  onStartFocus,
  onOpenTask,
  onStartEOD,
  onOpenEOD,
  onOpenDoneReport,
  onOpenPartials,
  onOpenBlocked,
  onRecoverTask,
  testId = 'focus-strip',
}) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-3"
      data-testid={testId}
    >
      <NowWidget task={focusTask} onStartFocus={onStartFocus} onOpenTask={onOpenTask} />
      <RecoveryWidget
        counts={counts}
        partialTasks={partialTasks}
        partialCount={partialCount}
        onOpenPartials={onOpenPartials}
        onOpenBlocked={onOpenBlocked}
        onRecoverTask={onRecoverTask}
      />
      <EODWidget
        eodSummary={eodSummary}
        onOpenEOD={onOpenEOD}
        onStartEOD={onStartEOD}
        onOpenDoneReport={onOpenDoneReport}
      />
    </div>
  );
}

export default FocusStripWidget;
